import { cn } from '@/lib/utils';
import { hashOf, textureFillFor } from './identityAccent';

/**
 * Two shapes behind a monogram's letter, placed from the entity's id.
 *
 * Thirty lines and no dependency, deliberately. The alternative considered was a
 * generated-avatar library; it would have brought unaudited colour into feature
 * code, put weight on the shell's above-the-fold bundle, and — because the art
 * is the library's, not ours — reshuffled every workspace's mark on a version
 * bump. That last one is the same defect as seeding on a name, arriving through
 * the lockfile instead of through a rename.
 *
 * Purely decorative: `aria-hidden`, and the letter it sits behind is what names
 * the workspace. Nothing here may become the only thing identifying anything,
 * which is why this is not used at 20px on a task card — at that size it is
 * noise under a letter rather than texture behind one.
 *
 * The parent clips it. Shapes are placed to cross the edges rather than sit
 * politely inside, so what shows is a fragment of something larger; a shape
 * fully contained in a 44px square reads as a sticker.
 */
export function IdentityTexture({
  id,
  className,
}: {
  id: string;
  className?: string;
}) {
  const hash = hashOf(id);
  const fill = textureFillFor(id);

  // Disjoint bit ranges, so the two shapes cannot move in lockstep as the hash
  // changes — which is what makes eight slots look like more than eight marks.
  const cx = 4 + ((hash >>> 3) % 40);
  const cy = 2 + ((hash >>> 8) % 36);
  const r = 11 + ((hash >>> 13) % 13);
  const x = -4 + ((hash >>> 17) % 40);
  const y = 20 + ((hash >>> 22) % 26);
  const size = 14 + ((hash >>> 26) % 16);

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 48 48"
      className={cn(
        'pointer-events-none absolute inset-0 size-full',
        className,
      )}
    >
      <circle cx={cx} cy={cy} r={r} className={fill} />
      <rect x={x} y={y} width={size} height={size} rx={4} className={fill} />
    </svg>
  );
}
